import React, { CSSProperties, useEffect, useState } from "react";
import { Input, Select, Space, Button, Switch, Typography } from "antd";
import {
  DeleteOutlined,
  CopyOutlined,
  DownOutlined,
  RightOutlined,
  CloseOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
  CheckSquareOutlined,
  FontSizeOutlined,
  PictureOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HolderOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { v4 as uuidv4 } from "uuid";

import type {
  QuestionData,
  QuestionType,
  Answer,
} from "../../../../types/survey";
import { LogicJumpModal } from "../LogicJumpModal";

const { Text } = Typography;
const { Option } = Select;

const iconColor: CSSProperties = { color: "#00000040" };

const QUESTION_TYPE_ICONS: Record<string, React.ReactNode> = {
  toggle: <CheckCircleOutlined style={{ fontSize: 14, color: "#00000040" }} />,
  singleSelect: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      style={{ display: "block" }}
    >
      <circle cx="8" cy="8" r="8" fill="white" />
      <circle
        cx="8"
        cy="8"
        r="7.25"
        stroke="black"
        strokeOpacity="0.25"
        strokeWidth="1.5"
      />
    </svg>
  ),
  multiSelect: (
    <CheckSquareOutlined style={{ fontSize: 14, color: "#00000040" }} />
  ),
  text: <FontSizeOutlined style={{ fontSize: 14, color: "#00000040" }} />,
  image: <PictureOutlined style={{ fontSize: 14, color: "#00000040" }} />,
  date: <CalendarOutlined style={{ fontSize: 14, color: "#00000040" }} />,
  time: <ClockCircleOutlined style={{ fontSize: 14, color: "#00000040" }} />,
  // number: <DatabaseOutlined style={{ fontSize: 14, color: "#00000040" }} />,
};

const QUESTION_TYPE_OPTIONS: Array<{ value: QuestionType; label: string }> = [
  { value: "toggle", label: "Yes / No question" },
  { value: "singleSelect", label: "Radio" },
  { value: "multiSelect", label: "Checkbox" },
  { value: "text", label: "Text" },
  { value: "image", label: "Photo" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  // { value: "number", label: "Number" },
];

const isOtherAnswer = (title: string): boolean => {
  const lower = title.toLowerCase();
  return (
    lower.includes("other") ||
    lower.includes("другое") ||
    lower.includes("інше")
  );
};

export const isCustomOptionAnswer = (answer: Answer): boolean => {
  return !!(
    answer.options?.multiline === true &&
    typeof answer.options?.minLength === "number" &&
    answer.options.minLength > 0 &&
    typeof answer.options?.maxLength === "number" &&
    answer.options.maxLength > 0
  );
};

interface HeaderProps {
  question: QuestionData;
  isExpanded: boolean;
  onDelete: () => void;
  onCopy?: () => void;
  index?: number;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
  };
  hasError?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  question,
  isExpanded,
  onDelete,
  onCopy,
  dragHandleProps,
  hasError,
}) => {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {isExpanded ? (
        <DownOutlined
          style={{
            color: "#000000E0",
            marginRight: 12,
            fontSize: 12,
          }}
        />
      ) : (
        <RightOutlined
          style={{
            color: "#000000E0",
            marginRight: 12,
            fontSize: 12,
          }}
        />
      )}

      <div
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        style={{
          color: "#999",
          marginRight: 12,
          cursor: "grab",
          display: "flex",
          alignItems: "center",
        }}
      >
        <HolderOutlined />
      </div>

      <Text
        style={{
          flex: 1,
        }}
      >
        {question.title || "Question title"}
      </Text>
      {question.required && <span style={{ color: "#ff4d4f" }}>*</span>}
      <Space onClick={(e) => e.stopPropagation()}>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onCopy?.();
          }}
        />
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        />
      </Space>
    </div>
  );
};

interface ContentProps {
  question: QuestionData;
  onChange: (updates: Partial<QuestionData>) => void;
  onDelete: () => void;
  onCopy?: () => void;
  index?: number;
  availableQuestions?: { id: string; title: string }[];
  error?: string;
}

const Content: React.FC<ContentProps> = ({
  question,
  onChange,
  onCopy,
  availableQuestions = [],
  error,
}) => {
  const [logicModalOpen, setLogicModalOpen] = useState(false);

  const hasCustomOption = (question.answers || []).some(isCustomOptionAnswer);

  // const handleTypeChange = (type: QuestionType) => {
  //   let updates: Partial<QuestionData> = { type, logic: [] };

  //   if (type === "toggle") {
  //     updates.answers = [
  //       {
  //         id: uuidv4(),
  //         title: "Yes",
  //         sort: 0,
  //         options: { enabled: false },
  //         logic: [],
  //       },
  //       {
  //         id: uuidv4(),
  //         title: "No",
  //         sort: 1,
  //         options: { enabled: false },
  //         logic: [],
  //       },
  //     ];
  //   } else if (type === "singleSelect" || type === "multiSelect") {
  //     if (!question.answers || question.answers.length < 2) {
  //       updates.answers = [
  //         {
  //           id: uuidv4(),
  //           title: "",
  //           sort: 0,
  //           options: { enabled: false },
  //           logic: [],
  //         },
  //         {
  //           id: uuidv4(),
  //           title: "",
  //           sort: 1,
  //           options: { enabled: false },
  //           logic: [],
  //         },
  //       ];
  //     }
  //   } else {
  //     updates.answers = [];
  //   }

  //   onChange(updates);
  // };
  const handleTypeChange = (type: QuestionType) => {
    let updates: Partial<QuestionData> = { type, logic: [] };

    if (type === "toggle") {
      // Для toggle всегда создаём Yes/No
      updates.answers = [
        {
          id: uuidv4(),
          title: "Yes",
          sort: 0,
          options: { enabled: false },
          logic: [],
        },
        {
          id: uuidv4(),
          title: "No",
          sort: 1,
          options: { enabled: false },
          logic: [],
        },
      ];
    } else if (type === "singleSelect" || type === "multiSelect") {
      // ✅ ИСПРАВЛЕНИЕ: Всегда сбрасываем ответы при переходе с toggle
      // или если текущие ответы не подходят (пустые или это Yes/No)
      const currentAnswers = question.answers || [];
      const isFromToggle =
        currentAnswers.length === 2 &&
        currentAnswers.some((a) => a.title === "Yes") &&
        currentAnswers.some((a) => a.title === "No");

      const needsReset =
        isFromToggle ||
        currentAnswers.length < 2 ||
        currentAnswers.every((a) => !a.title);

      if (needsReset) {
        updates.answers = [
          {
            id: uuidv4(),
            title: "",
            sort: 0,
            options: { enabled: false },
            logic: [],
          },
          {
            id: uuidv4(),
            title: "",
            sort: 1,
            options: { enabled: false },
            logic: [],
          },
        ];
      }
    } else {
      updates.answers = [];
    }

    onChange(updates);
  };

  const addAnswer = () => {
    const newAnswer: Answer = {
      id: uuidv4(),
      title: "",
      sort: question.answers?.length || 0,
      options: { enabled: false },
      logic: [],
    };
    onChange({ answers: [...(question.answers || []), newAnswer] });
  };

  const updateAnswer = (index: number, title: string) => {
    const newAnswers = [...(question.answers || [])];
    newAnswers[index] = { ...newAnswers[index], title };
    onChange({ answers: newAnswers });
  };

  const removeAnswer = (index: number) => {
    const newAnswers = (question.answers || [])
      .filter((_, i) => i !== index)
      .map((a, idx) => ({ ...a, sort: idx }));
    onChange({ answers: newAnswers });
  };

  const showAnswers =
    question.type === "singleSelect" ||
    question.type === "multiSelect" ||
    question.type === "toggle";

  const showLogicJumps =
    question.type === "singleSelect" || question.type === "toggle";
  const logicCount =
    question.answers?.filter((a) => a.logic?.[0]?.actions?.[0]?.targetFieldId)
      .length || 0;
  const totalAnswers =
    question.type === "toggle"
      ? 2
      : question.answers?.filter((a) => a.title).length || 0;

  const getTargetLabel = (targetId: string | undefined): string => {
    if (!targetId || targetId === "__next__") return "Next question";
    if (targetId === "__end__") return "End survey";
    const target = availableQuestions.find((q) => q.id === targetId);
    return target?.title || "Unknown";
  };

  const getTargetFromAnswer = (answer: Answer): string | undefined =>
    answer.logic?.[0]?.actions?.[0]?.targetFieldId;

  const handleSaveLogic = (nextAnswers: Answer[]) => {
    onChange({ answers: nextAnswers });
  };

  const handleCustomOptionChange = (checked: boolean) => {
    if (checked) {
      const isImageType = question.type === "image";

      const otherAnswer: Answer = {
        id: uuidv4(),
        title: isImageType ? "Description" : "Other (please specify)",
        sort: question.answers?.length || 0,
        options: {
          enabled: true,
          multiline: true,
          minLength: 3,
          maxLength: 250,
        },
        logic: [],
      };
      onChange({
        answers: [...(question.answers || []), otherAnswer],
      });
    } else {
      const filteredAnswers = (question.answers || []).filter(
        (a) => !isCustomOptionAnswer(a) && !isOtherAnswer(a.title),
      );
      onChange({
        answers: filteredAnswers.map((a, idx) => ({ ...a, sort: idx })),
      });
    }
  };

  return (
    <div>
      <div>
        <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <Text style={{ display: "block", marginBottom: 4 }}>Question</Text>
            <Input
              value={question.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="Input question"
              style={{
                background: "#fff",
                borderColor: error ? "#ff4d4f" : undefined,
              }}
              status={error ? "error" : undefined}
            />
            {error && <span style={{ color: "#ff4d4f" }}>{error}</span>}
          </div>
          <div style={{ width: 160 }}>
            <Text style={{ display: "block", marginBottom: 4 }}>
              Question type
            </Text>
            <Select
              value={question.type}
              onChange={handleTypeChange}
              style={{ width: "100%" }}
              placeholder="Select"
              optionLabelProp="label"
              dropdownMatchSelectWidth={false}
            >
              {QUESTION_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "16px 1fr",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {QUESTION_TYPE_ICONS[opt.value]}
                    </div>
                    <div>{opt.label}</div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {showAnswers && (
          <div style={{ marginBottom: 16 }}>
            {(question.answers || []).map((answer, idx) => {
              const targetId = getTargetFromAnswer(answer);

              return (
                <div
                  key={answer.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 24,
                    marginBottom: 8,
                  }}
                >
                  {question.type === "toggle" ? (
                    <div
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        background: "#0000000A",
                        borderRadius: 6,
                        color: "#00000040",
                      }}
                    >
                      {answer.title}
                    </div>
                  ) : (
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={answer.title}
                        onChange={(e) => updateAnswer(idx, e.target.value)}
                        placeholder={`Option ${idx + 1}`}
                        style={{ flex: 1, width: "100%" }}
                      />
                      <div
                        role="button"
                        tabIndex={-1}
                        onClick={() => removeAnswer(idx)}
                        style={{
                          width: "32px",
                          display: "flex",
                          justifyContent: "center",
                          border: "1px solid #00000026",
                          borderTopRightRadius: 6,
                          borderBottomRightRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        <CloseOutlined />
                      </div>
                    </Space.Compact>
                  )}
                  {showLogicJumps && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "5px 8px",
                        gap: 8,
                        fontSize: 14,
                        minWidth: 160,
                        height: 26,
                        borderRadius: 6,
                        backgroundColor: "#0000000F",
                      }}
                    >
                      <ArrowRightOutlined />
                      <Text
                        ellipsis
                        style={{ fontSize: 14, borderRadius: 4, maxWidth: 124 }}
                      >
                        {getTargetLabel(targetId)}
                      </Text>
                    </div>
                  )}
                </div>
              );
            })}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 16,
              }}
            >
              {showLogicJumps && totalAnswers > 0 && (
                <Button
                  style={{
                    borderColor: "#d9d9d9",
                    background: "#fff",
                    height: 32,
                  }}
                  onClick={() => setLogicModalOpen(true)}
                >
                  {logicCount > 0 ? "Edit logic jump" : "Add logic jump"}
                  {logicCount > 0 && (
                    <span
                      style={{
                        background: "#9A292B",
                        color: "#fff",
                        padding: "0 6px",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    >
                      {logicCount}/{totalAnswers}
                    </span>
                  )}
                </Button>
              )}
              {question.type !== "toggle" && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={addAnswer}
                  style={{
                    height: 32,
                    borderColor: "#d9d9d9",
                    background: "#fff",
                  }}
                >
                  Add answer
                </Button>
              )}
              {(question.type === "singleSelect" ||
                question.type === "multiSelect") && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Switch
                      checked={hasCustomOption}
                      onChange={handleCustomOptionChange}
                    />
                    <Text style={{ fontSize: 14 }}>Custom answer</Text>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
      {/* {question.type === "image" && (
        <div style={{ marginBottom: 16 }}>
          {hasCustomOption && (
            <Input
              value={question.answers[0].title}
              onChange={(e) => {
                const newAnswers = [...(question.answers || [])];

                newAnswers[0] = {
                  ...newAnswers[0],
                  title: e.target.value,
                };

                onChange({ answers: newAnswers });
              }}
              placeholder="Enter custom answer"
              style={{
                marginBottom: 16,
                width: "100%",
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Switch
              checked={hasCustomOption}
              onChange={handleCustomOptionChange}
            />
            <Text style={{ fontSize: 14 }}>Custom answer</Text>
          </div>
        </div>
      )} */}

      {showLogicJumps && (
        <LogicJumpModal
          open={logicModalOpen}
          onClose={() => setLogicModalOpen(false)}
          onSave={handleSaveLogic}
          question={question}
          availableQuestions={availableQuestions}
        />
      )}
    </div>
  );
};

export const QuestionEditor = {
  Header,
  Content,
};
